import { z } from 'zod';

/**
 * The self-serve DATA EXPORT archive contract (compliance.md § "Data rights";
 * auth.md § "Data rights — self-serve"). ONE source of truth for the shape of
 * the user's downloadable data bundle, validated with Zod so a malformed
 * repository projection or a future shape drift is caught at the boundary rather
 * than reaching the download unchecked — `@4f/backend`'s assembler parses the
 * fully-built archive against {@link dataExportArchiveSchema} before returning
 * it, and consumers (edge, runner, tests) share the inferred types.
 *
 * Every timestamp is an ISO string (the bundle serializes cleanly); SECRETS are
 * never modeled here (`password_hash`, `google_sub`, the CCBill subscription
 * ref, raw e2 credentials) so they cannot even be represented.
 */

/** The archive schema tag — bump when the shape changes so a consumer can branch. */
export const DATA_EXPORT_SCHEMA = 'data-rights.export/v1' as const;

/** Structured jsonb (persona / appearance / memory content) — an open string-keyed record. */
const jsonRecord = z.record(z.string(), z.unknown());

/** One user's non-secret profile fields (their own email is theirs; secrets are not). */
export const DataExportUserProfile = z.object({
  id: z.string(),
  email: z.string(),
  displayName: z.string(),
  locale: z.string().nullable(),
  timezone: z.string().nullable(),
  theme: z.string(),
  referralCode: z.string(),
  referredByUserId: z.string().nullable(),
  ageVerifiedAt: z.string().nullable(),
  createdAt: z.string(),
});
export type DataExportUserProfile = z.infer<typeof DataExportUserProfile>;

/** The account-level profile block. */
export const DataExportProfile = z.object({
  accountId: z.string(),
  tier: z.string(),
  creditBalanceMicros: z.number(),
  disabledAt: z.string().nullable(),
  createdAt: z.string(),
  users: z.array(DataExportUserProfile),
});
export type DataExportProfile = z.infer<typeof DataExportProfile>;

/** A companion (girlfriend) as exported — persona + appearance are the user's builder inputs. */
export const DataExportCompanion = z.object({
  id: z.string(),
  name: z.string(),
  persona: jsonRecord,
  appearance: jsonRecord,
  nsfwLevel: z.string(),
  status: z.string(),
  buildSource: z.string(),
  seed: z.number().nullable(),
  createdAt: z.string(),
  updatedAt: z.string(),
});
export type DataExportCompanion = z.infer<typeof DataExportCompanion>;

/** One message within an exported conversation (the raw AI-SDK content parts are kept verbatim). */
export const DataExportMessage = z.object({
  id: z.string(),
  role: z.string(),
  origin: z.string(),
  content: z.array(z.unknown()),
  createdAt: z.string(),
});
export type DataExportMessage = z.infer<typeof DataExportMessage>;

/** A conversation plus its full message history. */
export const DataExportConversation = z.object({
  id: z.string(),
  girlfriendId: z.string(),
  title: z.string().nullable(),
  archivedAt: z.string().nullable(),
  createdAt: z.string(),
  updatedAt: z.string(),
  messages: z.array(DataExportMessage),
});
export type DataExportConversation = z.infer<typeof DataExportConversation>;

/** One durable memory (inferred fact/preference/summary) as exported — the user's own recalled data. */
export const DataExportMemory = z.object({
  id: z.string(),
  girlfriendId: z.string(),
  kind: z.string(),
  content: jsonRecord,
  createdAt: z.string(),
});
export type DataExportMemory = z.infer<typeof DataExportMemory>;

/** A media MANIFEST entry — the e2 object key + metadata, never the bytes (fetched via the app). */
export const DataExportMediaAsset = z.object({
  id: z.string(),
  girlfriendId: z.string(),
  // Null for a pre-generated prebuilt-catalog asset (no originating user job).
  jobId: z.string().nullable(),
  kind: z.string(),
  e2Key: z.string(),
  contentType: z.string(),
  bytes: z.number(),
  width: z.number(),
  height: z.number(),
  durationMs: z.number().nullable(),
  visibility: z.string(),
  createdAt: z.string(),
});
export type DataExportMediaAsset = z.infer<typeof DataExportMediaAsset>;

/** A credit-ledger entry (the financial statement line). */
export const DataExportLedgerEntry = z.object({
  id: z.string(),
  deltaMicros: z.number(),
  reason: z.string(),
  refId: z.string().nullable(),
  balanceAfterMicros: z.number(),
  createdAt: z.string(),
});
export type DataExportLedgerEntry = z.infer<typeof DataExportLedgerEntry>;

/** A metered usage event (our upstream cost record). */
export const DataExportUsageEvent = z.object({
  id: z.string(),
  girlfriendId: z.string().nullable(),
  modelId: z.string().nullable(),
  tokenIn: z.number().nullable(),
  tokenOut: z.number().nullable(),
  costMicros: z.number(),
  kind: z.string(),
  createdAt: z.string(),
});
export type DataExportUsageEvent = z.infer<typeof DataExportUsageEvent>;

/** A subscription record (the CCBill provider ref is internal and omitted). */
export const DataExportSubscription = z.object({
  id: z.string(),
  tier: z.string(),
  status: z.string(),
  periodStart: z.string().nullable(),
  periodEnd: z.string().nullable(),
  createdAt: z.string(),
  updatedAt: z.string(),
});
export type DataExportSubscription = z.infer<typeof DataExportSubscription>;

/** The archive envelope metadata — schema tag, identity, and the truncation contract. */
export const DataExportMeta = z.object({
  schema: z.literal(DATA_EXPORT_SCHEMA),
  exportId: z.string(),
  accountId: z.string(),
  generatedAt: z.string(),
  /** The per-table row cap the assembly applied (financial history is bounded, never silently). */
  rowLimit: z.number(),
  /**
   * Whether each capped dataset ACTUALLY hit `rowLimit` and was trimmed. `true`
   * ⇒ incomplete for that dataset (page + re-run); `false` ⇒ complete.
   *
   * `messages` is per-CONVERSATION: `true` when at least one thread was trimmed.
   * The message history is capped for the same reason the financial tables are —
   * the archive is assembled and serialized whole, so an unbounded read is one
   * request that can exhaust the edge — and, like them, the cap is never silent.
   *
   * `media` (the manifest over `media_assets`) is the same contract, added after
   * the fact: it is OPTIONAL only so an archive written before the manifest was
   * capped still parses. Absent ⇒ that archive's manifest was read UNBOUNDED and
   * is therefore complete; every archive this code produces states the flag
   * explicitly. Assemblers must set it (`toDataExportArchive` requires it).
   */
  truncated: z.object({
    ledger: z.boolean(),
    usage: z.boolean(),
    messages: z.boolean(),
    media: z.boolean().optional(),
  }),
});
export type DataExportMeta = z.infer<typeof DataExportMeta>;

/**
 * The confirmation a DELETION REQUEST returns (compliance.md § "Data rights":
 * `soft-disable → grace → hard purge`). Shared by the consumer route, the staff
 * trigger, and the SPA so all three describe the same schedule.
 *
 * `disabledAt` is the grace clock's start — the persisted `accounts.disabled_at`,
 * never a locally fabricated instant — and `purgeEligibleAt` is the earliest the
 * irreversible purge may run. `alreadyScheduled` is `true` when the account was
 * ALREADY disabled (an idempotent re-request, or a prior moderator ban), in which
 * case the clock is the ORIGINAL stamp, not this request's.
 */
export const DeletionScheduleView = z.object({
  status: z.literal('scheduled'),
  accountId: z.string(),
  disabledAt: z.string(),
  purgeEligibleAt: z.string(),
  alreadyScheduled: z.boolean(),
});
export type DeletionScheduleView = z.infer<typeof DeletionScheduleView>;

/** The complete downloadable archive — parsed before it ever reaches the user. */
export const dataExportArchiveSchema = z.object({
  meta: DataExportMeta,
  profile: DataExportProfile,
  companions: z.array(DataExportCompanion),
  conversations: z.array(DataExportConversation),
  memories: z.array(DataExportMemory),
  media: z.array(DataExportMediaAsset),
  ledger: z.array(DataExportLedgerEntry),
  usage: z.array(DataExportUsageEvent),
  subscriptions: z.array(DataExportSubscription),
});
export type DataExportArchive = z.infer<typeof dataExportArchiveSchema>;
